import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';

/**
 * The public front page.
 *
 * One petrol field, the mark, one sentence, two doors. The previous version argued its case in
 * three columns of prose on white; this one does not argue at all — it says what the thing is and
 * gets out of the way, and the argument lives behind "Learn more" for the person who wants it.
 *
 * Lemon appears exactly once, as the mark's crossbar, which is the one place it is allowed to mean
 * nothing (docs/IDENTITY.md: lemon means overdue or a destructive confirmation, never decoration).
 * The buttons are therefore white — filled for the way in, outlined for the way to read more.
 */

function Midgard() {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-petrol" />;
  }
  if (currentUser) return <Navigate to="/desk" replace />;

  return (
    <div className="min-h-screen bg-petrol text-white flex flex-col">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
        <Logo size={28} showText />
        <Link
          to="/login"
          className="text-body tracking-[0.08em] uppercase hover:opacity-70 transition-opacity"
        >
          Log in
        </Link>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-20">
        {/* The mark at full size — the barcode H is the identity, so let it be the image. */}
        <Logo size={180} variant="numerals" className="justify-center mb-10 sm:mb-14" />

        <h1 className="font-display text-[34px] sm:text-[52px] leading-[1.1] max-w-3xl">
          An integrated library system
          <br className="hidden sm:block" />
          {' '}built for schools.
        </h1>

        <p className="text-input mt-6 max-w-xl opacity-80">
          Cataloguing, circulation, overdues and bookings — one system, built around the way a
          school library actually runs.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
          <Link
            to="/learn-more"
            className="bg-white text-petrol text-input px-8 py-3 rounded-[2px] hover:opacity-90 transition-opacity"
          >
            Learn more
          </Link>
          <Link
            to="/login"
            className="border border-white/60 text-white text-input px-8 py-3 rounded-[2px] hover:bg-white/10 transition-colors"
          >
            Log in
          </Link>
        </div>
      </main>

      <footer className="px-6 sm:px-10 py-6 border-t border-white/15 flex flex-wrap items-center justify-between gap-3">
        <span className="text-label opacity-60">Heimdl Catalog</span>
        <Link to="/calendar" className="text-label opacity-60 hover:opacity-100 transition-opacity">
          Teacher booking portal
        </Link>
      </footer>
    </div>
  );
}

export default Midgard;
